/**
 * Gong Audio Player
 * Healing Sounds
 */
class GongPlayer {
  constructor(container) {
    this.container = container;
    this.items = container.querySelectorAll('.hs-gong-item');
    this.stopBtn = container.querySelector('.hs-gong-stop__btn');
    
    this.currentAudio = null;
    this.currentItem = null;
    
    this.init();
  }

  init() {
    // Play buttons
    this.items.forEach(item => {
      const playBtn = item.querySelector('.hs-gong-item__play');
      playBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.togglePlay(item);
      });
      
      // Click on item (but not progress bar) to play
      item.addEventListener('click', (e) => {
        if (!e.target.closest('.hs-gong-item__progress')) {
          this.togglePlay(item);
        }
      });

      // Progress bar seek
      const progressBar = item.querySelector('.hs-gong-item__progress');
      progressBar.addEventListener('click', (e) => {
        e.stopPropagation();
        this.seek(item, e);
      });
    });

    // Stop button
    if (this.stopBtn) {
      this.stopBtn.addEventListener('click', () => this.stopAll());
    }

    // SVG icons
    this.pauseSvg = '<svg viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
    this.playSvg = '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>';
  }

  seek(item, e) {
    // Only seek if this item is currently playing
    if (this.currentItem !== item || !this.currentAudio) {
      // If not playing, start playing first then seek
      this.togglePlay(item);
      // Wait for audio to load before seeking
      if (this.currentAudio) {
        this.currentAudio.addEventListener('canplay', () => {
          this.performSeek(item, e);
        }, { once: true });
      }
      return;
    }
    
    this.performSeek(item, e);
  }

  performSeek(item, e) {
    if (!this.currentAudio || !this.currentAudio.duration) return;
    
    const progressBar = item.querySelector('.hs-gong-item__progress');
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    
    this.currentAudio.currentTime = percent * this.currentAudio.duration;
  }

  togglePlay(item) {
    // If clicking same item that's playing, pause it
    if (this.currentItem === item && this.currentAudio && !this.currentAudio.paused) {
      this.currentAudio.pause();
      return;
    }

    // If clicking same item that's paused, resume it
    if (this.currentItem === item && this.currentAudio && this.currentAudio.paused) {
      this.currentAudio.play().catch(err => console.error('Resume error:', err));
      return;
    }

    // Stop any current playback (different item)
    this.stopAll();

    // Create new audio
    const src = item.dataset.src;
    if (!src) {
      console.error('No audio source for', item.dataset.size);
      return;
    }

    this.currentItem = item;
    this.currentAudio = new Audio(src);
    
    item.classList.add('loading');

    // Event: can play
    this.currentAudio.addEventListener('canplay', () => {
      item.classList.remove('loading');
    }, { once: true });

    // Event: playing
    this.currentAudio.addEventListener('play', () => {
      item.classList.add('playing');
      item.querySelector('.hs-gong-item__play').innerHTML = this.pauseSvg;
    });

    // Event: pause
    this.currentAudio.addEventListener('pause', () => {
      item.classList.remove('playing');
      item.querySelector('.hs-gong-item__play').innerHTML = this.playSvg;
    });

    // Event: ended
    this.currentAudio.addEventListener('ended', () => {
      this.resetItem(item);
      this.currentItem = null;
      this.currentAudio = null;
    });

    // Event: time update
    this.currentAudio.addEventListener('timeupdate', () => {
      this.updateProgress(item);
    });

    // Event: error
    this.currentAudio.addEventListener('error', () => {
      item.classList.remove('loading', 'playing');
      item.classList.add('error');
      console.error('Error loading audio:', src);
    });

    // Play
    this.currentAudio.play().catch(err => {
      console.error('Playback error:', err);
      item.classList.remove('loading');
    });
  }

  updateProgress(item) {
    if (!this.currentAudio) return;

    const progressBar = item.querySelector('.hs-gong-item__progress-bar');
    const timeDisplay = item.querySelector('.hs-gong-item__time');

    const current = this.currentAudio.currentTime;
    const duration = this.currentAudio.duration || 0;
    const percent = duration > 0 ? (current / duration) * 100 : 0;

    progressBar.style.width = percent + '%';
    timeDisplay.textContent = this.formatTime(current);
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  resetItem(item) {
    item.classList.remove('playing', 'loading');
    item.querySelector('.hs-gong-item__play').innerHTML = this.playSvg;
    item.querySelector('.hs-gong-item__progress-bar').style.width = '0%';
    item.querySelector('.hs-gong-item__time').textContent = '0:00';
  }

  stopAll() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }

    if (this.currentItem) {
      this.resetItem(this.currentItem);
      this.currentItem = null;
    }

    this.items.forEach(item => {
      item.classList.remove('playing', 'loading');
    });
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.hs-gong-player').forEach(container => {
    new GongPlayer(container);
  });
});

// Re-init on Shopify section load (Theme Editor)
document.addEventListener('shopify:section:load', (event) => {
  const container = event.target.querySelector('.hs-gong-player');
  if (container) {
    new GongPlayer(container);
  }
});

